import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AppContext } from '../../context/app-context';
import { hasAdminAccess } from '../../util/AccountUtils';
import Busy from '../../components/Busy';
import { createTimestampedFileName } from '../../util/FileUtils';
import {
  deleteReplacedListingImage,
  uploadGalleryImage,
  uploadListingImage,
} from '../../components/checkin/request/file-upload-requests';
import {
  getLocationForEdit,
  createLocationDraft,
  updateLocation,
  saveLocationGallery,
  updateLocationManager,
  activateLocation,
  deactivateLocation,
  submitLocationForReview,
} from '../../components/location/requests/location-requests';
import { createNewUser } from '../../components/user/request/user-requests';
import { upsertLocationManager } from '../../hooks/location/useLocationManagers';
import { createLocationFromAddress } from '../../util/AddressUtil';
import { buildLocationDraftPayload } from './locationDraftPayload';
import { hasEquipmentOverageRate, positiveOrNull } from './equipmentOverageRate';
import {
  preserveEnrichmentFields,
  preservePricingFields,
  resolveManagedSpaces,
  shouldDefaultDailyBookings,
  getPendingStepIndices,
  getStepPersistability,
} from './locationUpdateHelpers';
import { getErrorMessage, isSmsVerificationFailure } from '../../util/NetworkErrorUtil';
import {
  createEmptyLocationWizardData,
  getWizardSteps,
  LocationWizardData,
  WizardStepLabel,
  newEquipmentRowKey,
  WIZARD_STEP_INFO,
  WIZARD_STEP_LOCATION_SETTINGS,
  WIZARD_STEP_MANAGER,
  WIZARD_STEP_FEATURES,
} from './types';
import { getWizardFooterConfig, WizardFooterConfig } from './wizardFooterConfig';
import { validateStep, StepErrors } from './stepValidation';
import {
  getNewLocationManagerRole,
  isNewLocationManagerBlank,
  toNewLocationManagerUsername,
} from './newLocationManager';
import {
  useAddressConfirmationGate,
  needsAddressConfirmation,
  buildGeocodedAddressUpdate,
  type AddressConfirmationDialogState,
} from './useAddressConfirmationGate';
import { LocationDeactivateVO } from '../../types/model/valueObject/location/LocationDeactivateVO'; 

const LOCATIONS_PROFILE_PATH = '/locations-profile';

const SMS_VERIFICATION_FALLBACK_MESSAGE =
  'The location manager was saved, but text notifications could not be enabled because their '
  + 'phone number could not be verified for SMS.';

/** Result of attempting to persist a step. */
type StepSaveResult = 'saved' | 'invalid' | 'error';

/**
 * How a step save treats missing required fields.
 * - `enforce`: surface errors and block (Finish, Submit for Review, and any
 *   update to an APPROVED listing).
 * - `skipInvalid`: leave the step unsaved and still dirty, but never block and
 *   never surface anything (free step-indicator navigation and Back).
 * - `saveIncomplete`: persist anyway (Next, Save, Close → Save). Required fields
 *   gate submission, not persistence — a draft is incomplete by definition.
 *   Step 1 of a location that does not exist yet is the exception: it POSTs, so
 *   it still blocks, and it surfaces its errors like `enforce` would.
 */
type StepSaveMode = 'enforce' | 'skipInvalid' | 'saveIncomplete';

/** Reads a File as a data URL (required by the upload-url helpers). */
const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

interface UseCreateLocationWizardResult {
  isEdit: boolean;
  /** True when the current user is a SecurSpace admin (sees the extra Step 6). */
  isAdmin: boolean;
  loading: boolean;
  saving: boolean;
  data: LocationWizardData;
  currentStep: number;
  /** Ordered step labels for the current user's role (5 suppliers, 6 admins). */
  steps: WizardStepLabel[];
  /** Zero-based index of the final step for the current role. */
  lastStepIndex: number;
  /** Indices of steps that have been visited/saved. */
  visitedSteps: number[];
  /** Indices of visited steps that currently pass validation (rendered with a checkmark). */
  completedSteps: number[];
  stepErrors: StepErrors;
  listingFile: File | null;
  galleryFiles: File[];
  /** Saved gallery photos the supplier re-cropped, by stored file name. Each
   *  crop is uploaded on save and takes the original's place in the gallery. */
  galleryReplacements: Map<string, File>;
  /** Role/status-driven footer button configuration. */
  footerConfig: WizardFooterConfig;
  /** Merges `partial` into the wizard data. Accepts a plain partial object, or
   *  an updater function `(prev) => partial` for derived updates (e.g.
   *  filtering an array) that must read the latest state rather than a
   *  possibly-stale closure. */
  updateData: (
    partial: Partial<LocationWizardData> | ((prev: LocationWizardData) => Partial<LocationWizardData>)
  ) => void;
  /** Applies location-enrichment values and marks each owning step for
   *  persistence. Accepts a plain partial object, or — like `updateData` — an
   *  updater function `(prev) => partial` so the mapping (e.g.
   *  `mapEnrichmentToWizardData`) can derive from the freshest wizard state
   *  at merge time rather than a `data` snapshot captured when the async
   *  enrichment request started. */
  applyEnrichmentData: (
    partial: Partial<LocationWizardData> | ((prev: LocationWizardData) => Partial<LocationWizardData>)
  ) => void;
  setListingFile: (file: File | null) => void;
  setGalleryFiles: (files: File[]) => void;
  /** Stages `file` to replace the saved gallery photo `fileName` in place, or
   *  drops a staged replacement when `file` is null. */
  replaceGalleryImage: (fileName: string, file: File | null) => void;
  /** Primary footer action: save the current step then advance / finish. */
  saveDraftAndAdvance: () => Promise<void>;
  /** Secondary footer action: validate and save every pending step, staying on the current page. */
  save: () => Promise<void>;
  /** Validate the whole listing, save, then submit it to SecurSpace for review. */
  submitForReview: () => Promise<void>;
  /** Jump directly to a step, auto-saving the current step first. */
  goToStep: (targetStep: number) => Promise<void>;
  goBack: () => Promise<void>;
  /** True when the current step has UI edits that have not been synced to the
   *  backend yet. Drives the Close save/discard prompt. */
  isDirty: boolean;
  /** True while the address is being geocoded (checking address gate). */
  checkingAddress: boolean;
  /** State for the address confirmation dialog. */
  addressConfirmationDialog: AddressConfirmationDialogState;
  /** Called when the user picks an option in the address confirmation dialog. */
  handleAddressConfirm: (selection: 'suggested' | 'typed') => void;
  /** Called when the user cancels the address confirmation dialog. */
  handleAddressConfirmClose: () => void;
  /** Validate and save all pending wizard edits, then exit to Manage Locations. */
  saveAndExit: () => Promise<void>;
  /** Exit to Manage Locations without saving the current step's unsynced edits. */
  discardAndExit: () => void;
}

/**
 * Manages shared state and persistence for the Create/Edit Location wizard.
 *
 * Holds the cross-step form data, the active step, the visited-step set, and the
 * pending image files. Each step persists itself to the backend (POST for the
 * first save, PATCH thereafter) on navigation — whether via the footer Next
 * button or by clicking a step in the indicator. Intermediate saves never set
 * `live = false`, so editing a live location preserves its published status;
 * only an admin toggling "Location Live" in Step 6 changes it.
 */
export function useCreateLocationWizard(
  onError: (message: string) => void,
  onWarning: (message: string) => void = onError,
  onSuccess: (message: string) => void = () => {}
): UseCreateLocationWizardResult {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const appContext = useContext(AppContext);
  // In this app the context `user` object is the account (user.id is the account id).
  const accountId = (appContext as { user?: { id?: string } })?.user?.id;
  const subscriptionType = (appContext as { user?: { subscriptionType?: string } })?.user?.subscriptionType;
  const isAdmin = hasAdminAccess((appContext as { user?: { userType: string } })?.user);

  const isEdit = Boolean(locationId);

  // Admins get a sixth "Admin Settings" step. Computed at the wizard-controller
  // level so the step-indicator component stays purely presentational.
  const steps = useMemo(() => getWizardSteps(isAdmin), [isAdmin]);
  const lastStepIndex = steps.length - 1;

  const [loading, setLoading] = useState<boolean>(isEdit);
  const [saving, setSaving] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [stepErrors, setStepErrors] = useState<StepErrors>({});
  const [data, setData] = useState<LocationWizardData>(createEmptyLocationWizardData);
  // Mirrors `data` for callbacks that need the latest value at call time but
  // shouldn't be recreated on every field edit (e.g. saveDraftAndAdvance /
  // saveAndExit, which are passed down to footer buttons — depending on the
  // whole `data` object there would recreate them, and everything memoized
  // off them, on every keystroke in any step). Safe to write during render:
  // it doesn't affect this render's output, only future callback invocations.
  const dataRef = useRef(data);
  dataRef.current = data;
  const [listingFile, setListingFileState] = useState<File | null>(null);
  const [galleryFiles, setGalleryFilesState] = useState<File[]>([]);
  const [galleryReplacements, setGalleryReplacementsState] = useState<Map<string, File>>(() => new Map());
  // Which step indices have UI edits that haven't been synced to the backend
  // yet. Tracked per-step (rather than as one global flag) so persisting one
  // step's changes can never clear — and silently drop — unsynced edits that
  // belong to a different step (e.g. edits left on an invalid step that was
  // navigated away from without saving). A step is cleared after its own
  // successful save. Used to skip no-op PATCHes on Back/Next/step-jumps and to
  // prompt "save or discard" on Close.
  const [dirtySteps, setDirtySteps] = useState<Set<number>>(() => new Set());

  // Latest currentStep, readable from the otherwise-stable field/file mutators
  // so they can flag the step being edited without listing currentStep as a
  // dependency (which would churn their identity on every step change).
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;

  const markStepDirty = useCallback((step: number) => {
    setDirtySteps((prev) => (prev.has(step) ? prev : new Set(prev).add(step)));
  }, []);

  const markStepsDirty = useCallback((stepsToMark: number[]) => {
    setDirtySteps((prev) => {
      if (stepsToMark.every((step) => prev.has(step))) return prev;
      const next = new Set(prev);
      stepsToMark.forEach((step) => next.add(step));
      return next;
    });
  }, []);

  const clearStepDirty = useCallback((step: number) => {
    setDirtySteps((prev) => {
      if (!prev.has(step)) return prev;
      const next = new Set(prev);
      next.delete(step);
      return next;
    });
  }, []);

  // Opening "+ Add New Manager" marks the Manager step dirty like any edit, but a form nothing has
  // been typed into has nothing to save — and can't be saved — so it must not trigger the Close
  // "unsaved changes" prompt (whose Save would just fail) or keep Save enabled elsewhere.
  const newManagerBlank = Boolean(data.newLocationManager) && isNewLocationManagerBlank(data.newLocationManager!);
  useEffect(() => {
    if (newManagerBlank) clearStepDirty(WIZARD_STEP_MANAGER);
  }, [newManagerBlank, clearStepDirty, data.newLocationManager]);

  // Whether any step has unsynced edits. Drives the Close "save or discard"
  // prompt so leaving with pending changes anywhere is never silent.
  const isDirty = dirtySteps.size > 0;
  // In edit mode every step is pre-populated, so treat them all as visited
  // (checkmarked). In create mode only the first step starts visited.
  const [visitedSteps, setVisitedSteps] = useState<number[]>(() =>
    isEdit ? steps.map((_, index) => index) : [0]
  );
  // Steps advanced past via the footer's primary button (Next / Finish). Used to
  // check off the steps that have no required fields — Manager (3), Features (4),
  // and the admin-only Admin Settings (6) — which otherwise can't earn their
  // checkmark from validation alone. In create mode this starts empty so a blank
  // step stays unchecked until the user actively advances past it. In edit mode
  // every step was already completed when the location was first created, so all
  // steps start "advanced" and a fully-entered no-required-field step shows
  // checked on load instead of only after the user clicks Next through it again.
  const [advancedSteps, setAdvancedSteps] = useState<number[]>(() =>
    isEdit ? steps.map((_, index) => index) : []
  );

  // The step list can change after mount (e.g. AppContext.user loads and
  // `isAdmin` flips false → true, growing 5 → 6 steps). Keep visited/advanced
  // indices in sync: in edit mode every step is visited and pre-advanced;
  // otherwise drop any indices that no longer exist so the indicator's completed
  // state stays consistent.
  useEffect(() => {
    setVisitedSteps((prev) =>
      isEdit
        ? steps.map((_, index) => index)
        : prev.filter((index) => index >= 0 && index < steps.length)
    );
    setAdvancedSteps((prev) =>
      isEdit
        ? steps.map((_, index) => index)
        : prev.filter((index) => index >= 0 && index < steps.length)
    );
  }, [isEdit, steps]);

  // Tracks whether the auto-approve → daily-bookings default has already been
  // applied so it only fires once (on the first visit to Step 5).
  const dailyInitRef = useRef(false);

  // The location's last-persisted publish status. Used by the admin Step 6 save
  // to decide whether a deactivate call is needed (only a currently-live
  // location can be taken down — the API rejects deactivating a never-live
  // draft). Activation is always attempted when the toggle is on, regardless of
  // this value. Defaults to false for a new draft; set from the loaded record in
  // edit mode.
  const persistedLiveRef = useRef(false);

  // Synchronous re-entry guard for the submit-for-review flow. The `saving`
  // state flag doesn't update within the same tick, so a rapid double-click
  // could otherwise fire multiple save + submit sequences before the first
  // render commits. This ref flips synchronously and is cleared in `finally`.
  const submitInFlightRef = useRef(false);

  // --- Address confirmation gate ---
  const addressGate = useAddressConfirmationGate();
  // `runAddressGate`/`closeConfirmationDialog`/`clearPending` are memoized with
  // `[]` deps inside useAddressConfirmationGate, so their identities are stable
  // across renders — unlike `addressGate` itself, which is a fresh object
  // literal every render. Depending on these directly (instead of the whole
  // `addressGate` object) keeps callbacks that only need to call them from
  // being recreated on every unrelated re-render.
  const { runAddressGate, closeConfirmationDialog, clearPending, pendingGeocodedAddress, pendingTypedAddress } =
    addressGate;
  // Tracks a pending advance action that should fire after a confirmed address
  // update has been applied to `data` (which requires a React re-render before
  // the stale `data` closure refreshes). The useEffect below watches this ref
  // and triggers the advance once the new data is available.
  type PendingAdvanceAction = 'next' | 'save' | 'saveAndExit' | null;
  const pendingAdvanceActionRef = useRef<PendingAdvanceAction>(null);

  const handleAddressConfirm = useCallback(
    (selection: 'suggested' | 'typed') => {
      if (pendingGeocodedAddress) {
        const update = buildGeocodedAddressUpdate(pendingGeocodedAddress, pendingTypedAddress, selection);
        setData((prev) => ({ ...prev, ...update }));
        // Mark step dirty so the subsequent runStep actually persists.
        markStepDirty(WIZARD_STEP_INFO);
      }
      closeConfirmationDialog();
      clearPending();
      // The advance will fire from the useEffect once data re-renders with coords.
      // pendingAdvanceActionRef is already set by saveDraftAndAdvance/saveAndExit.
    },
    [pendingGeocodedAddress, pendingTypedAddress, closeConfirmationDialog, clearPending, markStepDirty],
  );

  const handleAddressConfirmClose = useCallback(() => {
    closeConfirmationDialog();
    pendingAdvanceActionRef.current = null;
  }, [closeConfirmationDialog]);

  // The location's last-persisted raw lifecycle status (`"DRAFT"`,
  // `"IN_REVIEW"`, `"APPROVED"`). Echoed back on every PATCH so the backend
  // doesn't reset an omitted status to DRAFT — which is what silently demoted a
  // Pending (IN_REVIEW) location to Draft when it was edited or closed out of.
  // Undefined for a brand-new draft (no status to preserve); set from the loaded
  // record in edit mode.
  const persistedStatusRef = useRef<string | undefined>(undefined);

  /**
   * Mode shared by every "save my work" path (Next, Save, Close → Save). The API
   * re-validates required fields on any update to an APPROVED listing, so
   * persisting one incomplete would surface as an opaque 400 instead of an
   * inline error.
   */
  const resolveSaveMode = useCallback(
    (): StepSaveMode => (persistedStatusRef.current === 'APPROVED' ? 'enforce' : 'saveIncomplete'),
    []
  );

  // A Close → Save can persist Info and then enrichment-dirtied later steps in
  // one async callback. Keep the newly-created ID in a ref so those subsequent
  // PATCHes do not wait for React's asynchronous `data.id` state update.
  const persistedLocationIdRef = useRef<string | undefined>(locationId);

  const markVisited = useCallback((stepIndex: number) => {
    setVisitedSteps((prev) => (prev.includes(stepIndex) ? prev : [...prev, stepIndex]));
  }, []);

  const markAdvanced = useCallback((stepIndex: number) => {
    setAdvancedSteps((prev) => (prev.includes(stepIndex) ? prev : [...prev, stepIndex]));
  }, []);

  // Whatever step becomes active is, by definition, visited.
  useEffect(() => {
    markVisited(currentStep);
  }, [currentStep, markVisited]);

  // Load existing location in edit mode.
  useEffect(() => {
    let cancelled = false;
    if (!locationId) return;

    setLoading(true);
    getLocationForEdit(locationId)
      .then((res: { body: Record<string, unknown> }) => {
        if (cancelled) return;
        const location = res.body || {};
        persistedLocationIdRef.current = location.id as string | undefined;
        // Remember the server's current publish status so the admin Step 6 save
        // only activates/deactivates when the toggle actually changes.
        persistedLiveRef.current = Boolean(location.live);
        // Remember the current lifecycle status so every PATCH can echo it back
        // and the backend keeps the location in its stage (e.g. IN_REVIEW stays
        // Pending) instead of resetting an omitted status to DRAFT.
        persistedStatusRef.current = (location.status as string) || undefined;
        const galleryItems = (location.locationGallery as { galleryImageFileName: string }[]) || [];
        const rawLat = location.addressLatitude;
        const rawLng = location.addressLongitude;
        const features = (location.locationFeatures as { feature?: string }[]) || [];
        const has247 = features.some((f) => f.feature === '24/7 Access');
        const rawManager = location.locationManager as { username: string; firstName: string; lastName: string; phoneNumber: string; isOptedIntoText: boolean; smsVerified?: boolean | null } | null;
        setData({
          ...createEmptyLocationWizardData(),
          ...location,
          addressLatitude: rawLat != null ? parseFloat(String(rawLat)) : null,
          addressLongitude: rawLng != null ? parseFloat(String(rawLng)) : null,
          locationAddress: createLocationFromAddress(location as never),
          gallery: galleryItems.map((g) => ({ fileName: g.galleryImageFileName })),
          locationDescription: (location.locationDescription as string) || '',
          instructions: (location.instructions as string) || '',
          has247Access: has247,
          gateHours: (location.gateHours as string) || '',
          afterHoursAccessProcedure: (location.afterHoursAccessProcedure as string) || '',
          minNumberOfSpaces: (location.minNumberOfSpaces as number) ?? null,
          totalNumberOfSpaces: (location.totalNumberOfSpaces as number) ?? null,
          managedLocation: Boolean(location.managedLocation),
          managedSpaces: (location.managedSpaces as number) ?? null,
          locationManager: rawManager ? {
            username: rawManager.username || '',
            firstName: rawManager.firstName || '',
            lastName: rawManager.lastName || '',
            phoneNumber: rawManager.phoneNumber || '',
            isOptedIntoText: !!rawManager.isOptedIntoText,
            smsVerified: rawManager.smsVerified ?? null,
          } : null,
          pricePerDay: (location.pricePerDay as number) > 0 ? (location.pricePerDay as number) : null,
          pricePerMonth: (location.pricePerMonth as number) ?? null,
          overageRate: (location.overageRate as number) ?? null,
          chargeOverages: Boolean(location.chargeOverages),
          qrScannerAvailable: Boolean(location.qrScannerAvailable),
          live: Boolean(location.live),
          locationEquipmentTypes: ((location.locationEquipmentTypes as any[]) || []).map((eq) => ({
            id: eq.id ?? null,
            _key: newEquipmentRowKey(),
            equipmentType: eq.equipmentType || '',
            // $0 means "use the location's price" to the API, same as blank.
            pricePerDay: positiveOrNull(eq.pricePerDay),
            pricePerWeek: eq.pricePerWeek ?? null,
            pricePerMonth: positiveOrNull(eq.pricePerMonth),
            overageRate: hasEquipmentOverageRate(eq.overageRate) ? eq.overageRate : null,
          })),
        } as LocationWizardData);
      })
      .catch(() => {
        if (!cancelled) onError('Unable to load this location. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [locationId, onError]);

  const updateData = useCallback(
    (partial: Partial<LocationWizardData> | ((prev: LocationWizardData) => Partial<LocationWizardData>)) => {
      setData((prev) => ({ ...prev, ...(typeof partial === 'function' ? partial(prev) : partial) }));
      markStepDirty(currentStepRef.current);
    },
    [markStepDirty]
  );

  // Enrichment is initiated on Info (Step 1), but its response also owns fields
  // in Location Settings (Step 2) and Features (Step 4). Marking only the visible
  // step meant an Info auto-save cleared the sole dirty flag, so the later-step
  // values were neither PATCHed nor included in Close's unsaved-changes prompt.
  //
  // Accepts an updater function (mirroring `updateData`) so the caller can
  // build the enrichment partial from the freshest wizard state at merge
  // time — the enrichment request can take several seconds, and mapping
  // against a `data` snapshot captured when the request started could
  // silently discard any edit made while it was in flight.
  const applyEnrichmentData = useCallback((
    partial: Partial<LocationWizardData> | ((prev: LocationWizardData) => Partial<LocationWizardData>)
  ) => {
    setData((prev) => ({ ...prev, ...(typeof partial === 'function' ? partial(prev) : partial) }));
    markStepsDirty([WIZARD_STEP_INFO, WIZARD_STEP_LOCATION_SETTINGS, WIZARD_STEP_FEATURES]);
  }, [markStepsDirty]);

  // Public file setters that also flag the Info step (where the images live)
  // dirty so a subsequent Back / Next actually PATCHes the staged image change,
  // even when the change lands after the supplier has moved to another step. Internal post-save resets use
  // the raw state setters (`setListingFileState` / `setGalleryFilesState`)
  // directly so they don't re-flag a step that was just saved.
  const setListingFile = useCallback((file: File | null) => {
    setListingFileState(file);
    markStepDirty(WIZARD_STEP_INFO);
  }, [markStepDirty]);
  const setGalleryFiles = useCallback((files: File[]) => {
    setGalleryFilesState(files);
    markStepDirty(WIZARD_STEP_INFO);
  }, [markStepDirty]);
  const replaceGalleryImage = useCallback((fileName: string, file: File | null) => {
    setGalleryReplacementsState((current) => {
      const next = new Map(current);
      if (file) {
        next.set(fileName, file);
      } else {
        next.delete(fileName);
      }
      return next;
    });
    markStepDirty(WIZARD_STEP_INFO);
  }, [markStepDirty]);

  // When arriving at Step 5 (Pricing) for the first time on a new location,
  // default "Allow Daily Bookings" to Yes if auto-approve was enabled in Step 2
  // so the supplier doesn't have to toggle it manually.
  useEffect(() => {
    if (currentStep === 4 && !dailyInitRef.current) {
      dailyInitRef.current = true;
      setData((prev) => {
        if (shouldDefaultDailyBookings(isEdit, prev)) {
          // Applying the default is a real (unsynced) change, so flag dirty to
          // ensure the Pricing step actually PATCHes it on Next/Finish.
          markStepDirty(currentStep);
          return { ...prev, pricePerDay: 0 };
        }
        return prev;
      });
    }
  }, [currentStep, isEdit, markStepDirty]);

  // Once required-field errors are shown for a step, re-validate live as the user
  // edits so each error clears the moment its field is fixed (matches the sign-up
  // form behavior). Intentionally excludes `stepErrors` from deps to avoid a loop;
  // a fresh closure is created on every data/step change.
  useEffect(() => {
    setStepErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      return validateStep(currentStep, data);
    });
  }, [data, currentStep]);

  /**
   * Persists a single step to the backend and merges the response into wizard
   * state. Performs no client-side validation and no navigation; it throws on
   * network failure so the caller can surface an error and decide what to do.
   *
   * IMPORTANT: the Pricing step (4) intentionally omits `live` and the admin
   * fields so an intermediate save never republishes/unpublishes a location.
   * `live` is owned exclusively by the admin-only Step 6.
   *
   * CLOSURE-STALENESS INVARIANT: `goToStep`/`goBack`/`saveAndExit` can persist
   * several dirty steps back-to-back in one sequential loop, awaiting this same
   * `saveStepToBackend` closure on each iteration. That closure — and the `data`
   * it reads — is captured once, from the render active when the loop started;
   * it is NOT refreshed between iterations, even though each step's own
   * `setData` call resolves before the next iteration begins. Today this is
   * safe only because (1) the one cross-step value every later step needs —
   * the location `id` — is read from `persistedLocationIdRef` (a ref, always
   * live) rather than from `data`, and (2) `preserveEnrichmentFields` /
   * `preservePricingFields` re-pin the fields enrichment/pricing care about to
   * their pre-loop values on every merge, so no field a later iteration reads
   * can actually have drifted mid-loop. If a future step's payload ever needs
   * to read a value that an *earlier* step's own save is expected to compute
   * (anything other than `id`), it must be threaded through a ref the same
   * way — reading it from `data` here would silently use the stale pre-loop
   * snapshot instead of what the earlier step just persisted.
   */
  const saveStepToBackend = useCallback(async (
    stepIndex: number
  ): Promise<void> => {
    // Echoes the existing record's lifecycle status on every PATCH so the
    // backend keeps the location in its current stage. Omitting it makes the
    // backend reset the status to DRAFT, which silently demoted a Pending
    // (IN_REVIEW) location to Draft on any edit — including the auto-save that
    // Close performs. Empty for a brand-new draft (nothing to preserve yet).
    const statusFields = (): { status?: string } =>
      persistedStatusRef.current ? { status: persistedStatusRef.current } : {};

    // --- Step 1 (Info): images + draft create/update + gallery sync ---
    if (stepIndex === 0) {
      const previousListingImageFileName = data.listingImageFileName;
      let listingImageFileName = data.listingImageFileName;
      if (listingFile) {
        const dataUrl = await readFileAsDataUrl(listingFile);
        listingImageFileName = await uploadListingImage(
          dataUrl,
          createTimestampedFileName(listingFile.name),
          listingFile.type
        );
      }

      const persistedLocationId = persistedLocationIdRef.current || data.id;
      const isNew = !persistedLocationId;
      const payloadData = persistedLocationId && !data.id ? { ...data, id: persistedLocationId } : data;
      const payload = buildLocationDraftPayload(payloadData, accountId, listingImageFileName, persistedStatusRef.current);

      const savedLocation = (isNew
        ? (await createLocationDraft(payload)).body
        : (await updateLocation(persistedLocationId as string, payload)).body) as LocationWizardData;
      persistedLocationIdRef.current = savedLocation.id;

      const uploadGalleryFile = async (file: File): Promise<string> => uploadGalleryImage(
        // A saved location always has a server-assigned id
        savedLocation.id as string,
        await readFileAsDataUrl(file),
        createTimestampedFileName(file.name),
        file.type
      );
      // A re-cropped saved photo keeps its position; new photos go at the end.
      const galleryFileNames: string[] = [];
      for (const { fileName } of data.gallery || []) {
        const replacement = galleryReplacements.get(fileName);
        // eslint-disable-next-line no-await-in-loop
        galleryFileNames.push(replacement ? await uploadGalleryFile(replacement) : fileName);
      }
      for (const file of galleryFiles) {
        // eslint-disable-next-line no-await-in-loop
        galleryFileNames.push(await uploadGalleryFile(file));
      }

      await saveLocationGallery(savedLocation, galleryFileNames);

      if (
        listingFile &&
        previousListingImageFileName &&
        previousListingImageFileName !== listingImageFileName
      ) {
        deleteReplacedListingImage(previousListingImageFileName);
      }

      const savedLatRaw = savedLocation.addressLatitude;
      const savedLngRaw = savedLocation.addressLongitude;
      const parsedLat = savedLatRaw == null ? null : Number(savedLatRaw);
      const parsedLng = savedLngRaw == null ? null : Number(savedLngRaw);
      setData((prev) => ({
        ...prev,
        ...savedLocation,
        locationAddress: prev.locationAddress,
        addressLatitude: parsedLat != null && Number.isFinite(parsedLat) ? parsedLat : prev.addressLatitude,
        addressLongitude: parsedLng != null && Number.isFinite(parsedLng) ? parsedLng : prev.addressLongitude,
        listingImageFileName,
        gallery: galleryFileNames.map((fileName) => ({ fileName })),
        // The manager is only edited/persisted in Step 3. Preserve the local
        // selection so a stale `locationManager` echoed by other-step saves can't
        // resurrect a manager the user just removed.
        locationManager: prev.locationManager,
        // Carry forward the later-step fields (including any populated by the
        // admin "Autofill Location" enrichment). This Step-1 save only sends
        // Step-1 fields, so the server echoes these back as defaults/empty —
        // preserving `prev` keeps autofilled details alive when advancing to (or
        // jumping to) a later step.
        ...preserveEnrichmentFields(prev),
        ...preservePricingFields(prev),
      }));
      setListingFileState(null);
      setGalleryFilesState([]);
      setGalleryReplacementsState(new Map());
      return;
    }

    // --- Step 2 (Location Settings) ---
    if (stepIndex === 1) {
      // Merge the 24/7 toggle with any existing features so we don't clobber
      // features set in the Features step.
      const existing = data.locationFeatures || [];
      const others = existing.filter((f) => f.feature !== '24/7 Access');
      const features = data.has247Access ? [...others, { feature: '24/7 Access' }] : others;

      // Held in consts so the post-save merge can re-assert exactly what was sent.
      const sentGateHours = data.has247Access ? '' : data.gateHours;
      const sentAfterHoursAccessProcedure = data.has247Access ? '' : data.afterHoursAccessProcedure;

      // Send these as-is, never `|| null`: PATCH merge semantics treat null as
      // "not provided" (unchanged), so a cleared field would silently keep its
      // old value and the server would echo it straight back. Empty string is
      // what actually clears it — same rule as locationManagerUsername below.
      const payload: Record<string, unknown> = {
        locationDescription: data.locationDescription,
        instructions: data.instructions,
        // Gate hours and after-hours access only apply when the location is not
        // 24/7. Clear any previously stored values when 24/7 access is enabled.
        gateHours: sentGateHours,
        afterHoursAccessProcedure: sentAfterHoursAccessProcedure,
        minNumberOfSpaces: data.minNumberOfSpaces,
        totalNumberOfSpaces: data.totalNumberOfSpaces,
        locationFeatures: features,
        ...statusFields(),
      };
      const savedLocation = (await updateLocation((persistedLocationIdRef.current || data.id) as string, payload)).body as LocationWizardData;
      setData((prev) => ({
        ...prev,
        ...savedLocation,
        locationManager: prev.locationManager,
        ...preserveEnrichmentFields(prev),
        ...preservePricingFields(prev),
        // preserveEnrichmentFields re-asserts these from pre-save state, which is
        // right for steps that don't send them but wrong here: the 24/7 toggle
        // clears them without the user ever touching the fields, so `prev` still
        // holds the old text and would resurrect it — then write it back on the
        // next save. What was just persisted wins.
        gateHours: sentGateHours,
        afterHoursAccessProcedure: sentAfterHoursAccessProcedure,
      }));
      return;
    }

    // --- Step 3 (Manager) ---
    if (stepIndex === 2) {
      let manager = data.locationManager;
      let managerJustCreated = false;
      let smsVerificationFailed = false;

      // "+ Add New Manager": create the account user first, then assign them below like any
      // existing manager. The user is swapped into `locationManager` as soon as it exists, so if
      // the assignment fails the retry only re-assigns instead of trying to create it again.
      if (data.newLocationManager) {
        // Without an account the assignment below would send '' and silently clear the manager.
        if (!accountId) throw new Error('Unable to determine your account. Please refresh and try again.');
        const { firstName, lastName, phoneNumber, email, isOptedIntoText } = data.newLocationManager;
        const username = toNewLocationManagerUsername(email);
        try {
          await createNewUser({
            accountId,
            username,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phoneNumber,
            role: getNewLocationManagerRole(subscriptionType),
            // Empty = access to every location, matching "All locations" in Manage Users.
            locationIds: [],
            isOptedIntoText,
          });
        } catch (err) {
          // The API commits the user before verifying the number for SMS, so this is a partial
          // success: carry on with the assignment and only warn.
          if (!isSmsVerificationFailure(err)) throw err;
          smsVerificationFailed = true;
          onWarning(getErrorMessage(err, SMS_VERIFICATION_FALLBACK_MESSAGE));
        }
        manager = {
          username,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneNumber,
          isOptedIntoText,
          smsVerified: smsVerificationFailed ? false : null,
        };
        managerJustCreated = true;
        if (!smsVerificationFailed) {
          onSuccess(`${manager.firstName} ${manager.lastName} was added and will receive an email to create a password.`);
        }
        upsertLocationManager(accountId, manager);
        setData((prev) => ({ ...prev, locationManager: manager, newLocationManager: null }));
      }

      // Assigning a manager: the API resolves the flat `locationManagerUsername`
      // string to the manager relation.
      //
      // Removing a manager: the PATCH merge semantics treat a null field as
      // "not provided" (unchanged), so sending null never clears the manager.
      // An empty string is the backend's explicit "remove the location manager"
      // signal, so send '' when no manager is selected.
      const managerPayload: Record<string, unknown> = {
        locationManagerUsername: manager?.username ?? '',
        ...statusFields(),
      };
      const savedLocation = (await updateLocation((persistedLocationIdRef.current || data.id) as string, managerPayload)).body as LocationWizardData;

      // Persist the manager's contact details and text opt-in. The API verifies the number via
      // Twilio Lookup when they are newly opting in (or changed it) and responds with
      // SMS_VERIFICATION_FAILED when it cannot receive texts.
      //
      // That failure is deliberately non-blocking: being unreachable by SMS does not disqualify
      // someone from managing a location, so the assignment above stands and we only warn. The
      // opt-in and the failed verdict are already persisted server-side, so mirroring
      // smsVerified locally keeps the Phone field's warning visible for the rest of this session
      // (later visits pick it up from the manager record).
      // A manager that was just created already carries its phone and opt-in.
      if (manager && !managerJustCreated && accountId) {
        try {
          await updateLocationManager(accountId, {
            username: manager.username,
            firstName: manager.firstName,
            lastName: manager.lastName,
            phoneNumber: manager.phoneNumber,
            isOptedIntoText: manager.isOptedIntoText,
          });
        } catch (err) {
          if (!isSmsVerificationFailure(err)) throw err;
          smsVerificationFailed = true;
          onWarning(getErrorMessage(err, SMS_VERIFICATION_FALLBACK_MESSAGE));
        }
      }

      setData((prev) => ({
        ...prev,
        ...savedLocation,
        locationManager: prev.locationManager && smsVerificationFailed
          ? { ...prev.locationManager, smsVerified: false }
          : prev.locationManager,
        ...preserveEnrichmentFields(prev),
        ...preservePricingFields(prev),
      }));
      return;
    }

    // --- Step 4 (Features) ---
    if (stepIndex === 3) {
      const payload: Record<string, unknown> = {
        locationFeatures: data.locationFeatures || [],
        ...statusFields(),
      };
      const savedLocation = (await updateLocation((persistedLocationIdRef.current || data.id) as string, payload)).body as LocationWizardData;
      setData((prev) => ({ ...prev, ...savedLocation, locationManager: prev.locationManager, ...preserveEnrichmentFields(prev), ...preservePricingFields(prev) }));
      return;
    }

    // --- Step 5 (Pricing) ---
    // NOTE: deliberately excludes `live`, `chargeOverages`, and
    // `qrScannerAvailable`. Those are admin-only and saved in Step 6 so a
    // supplier's pricing save can never (un)publish a location.
    if (stepIndex === 4) {
      const payload: Record<string, unknown> = {
        managedLocation: data.managedLocation,
        managedSpaces: resolveManagedSpaces(data),
        pricePerDay: data.pricePerDay ?? 0,
        pricePerMonth: data.pricePerMonth ?? 0,
        overageRate: data.overageRate ?? 0,
        locationEquipmentTypes: (data.locationEquipmentTypes || []).map((eq) => ({
          id: eq.id || undefined,
          equipmentType: eq.equipmentType,
          pricePerDay: positiveOrNull(eq.pricePerDay),
          pricePerWeek: eq.pricePerWeek ?? null,
          pricePerMonth: positiveOrNull(eq.pricePerMonth),
          overageRate: hasEquipmentOverageRate(eq.overageRate) ? eq.overageRate : null,
        })),
        ...statusFields(),
      };
      const savedLocation = (await updateLocation((persistedLocationIdRef.current || data.id) as string, payload)).body as LocationWizardData;
      // Preserve local pricing intent (and `live`) over whatever the API echoes.
      setData((prev) => ({ ...prev, ...savedLocation, locationManager: prev.locationManager, ...preserveEnrichmentFields(prev), ...preservePricingFields(prev) }));
      return;
    }

    // --- Step 6 (Admin Settings, admins only) ---
    // Persists only the non-publish admin fields; this PATCH never touches
    // `live`. The Location Live toggle is committed separately via
    // reconcilePublishState() on Finish, so it always runs — even when these
    // fields are unchanged — and a failed activation is never silently skipped.
    if (stepIndex === 5) {
      const locationId = (persistedLocationIdRef.current || data.id) as string;

      const payload: Record<string, unknown> = {
        chargeOverages: data.chargeOverages,
        qrScannerAvailable: data.qrScannerAvailable,
        ...statusFields(),
      };
      const savedLocation = (await updateLocation(locationId, payload)).body as LocationWizardData;

      setData((prev) => ({
        ...prev,
        ...savedLocation,
        ...preserveEnrichmentFields(prev),
        ...preservePricingFields(prev),
        locationManager: prev.locationManager,
        chargeOverages: prev.chargeOverages,
        qrScannerAvailable: prev.qrScannerAvailable,
        live: prev.live,
      }));
    }
  }, [accountId, data, galleryFiles, galleryReplacements, listingFile, onSuccess, onWarning, subscriptionType]);

  /**
   * Validates and persists the given step.
   *
   * @param mode See {@link StepSaveMode}.
   * @param manageBusy When true (default), this call toggles `saving` /
   *        `Busy` itself around its own network request. Pass false when the
   *        caller (see {@link persistPendingSteps}) is already holding the
   *        Busy overlay open for a whole batch of steps — toggling it off and
   *        back on between every step in a multi-step save caused a visible
   *        flash and an extra pair of renders per step.
   */
  const runStep = useCallback(async (
    stepIndex: number,
    mode: StepSaveMode,
    manageBusy: boolean = true
  ): Promise<StepSaveResult> => {
    const hasLocationId = Boolean(persistedLocationIdRef.current || data.id);
    const persistability = getStepPersistability(
      stepIndex,
      data,
      dirtySteps.has(stepIndex),
      hasLocationId,
      { allowIncomplete: mode === 'saveIncomplete' }
    );

    // Set only when validation actually blocked the save. Reaching here under
    // `saveIncomplete` means the Step 1 create exception fired, so the user
    // still needs to be told why nothing happened — only free navigation,
    // which never asked to save, stays silent.
    if (persistability.validationErrors) {
      if (mode !== 'skipInvalid') {
        setStepErrors(persistability.validationErrors);
        onError(Object.values(persistability.validationErrors).join('\n'));
      }
      return 'invalid';
    }
    if (!persistability.incomplete) {
      setStepErrors({});
    }

    if (!persistability.willPersist) {
      // Blocked for a non-field reason (no location id yet, manager missing a
      // phone number) — stay silent only for free navigation, which never
      // asked to save.
      if (persistability.blockingMessage) {
        if (mode !== 'skipInvalid') {
          onError(persistability.blockingMessage);
        }
        return 'invalid';
      }
      // Otherwise the step is simply pristine — no unsynced edits. Back /
      // Next / step-jumps of a pristine step are a no-op that resolves
      // 'saved' without touching the UI state, so the Busy overlay never
      // flickers on no-op navigation. Dirtiness is checked per-step so a save
      // here never consumes another step's pending edits.
      return 'saved';
    }

    if (manageBusy) {
      setSaving(true);
      Busy.set(true);
    }
    try {
      await saveStepToBackend(stepIndex);
      clearStepDirty(stepIndex);
      return 'saved';
    } catch (err) {
      onError(getErrorMessage(err, 'Something went wrong saving your changes. Please try again.'));
      return 'error';
    } finally {
      if (manageBusy) {
        setSaving(false);
        Busy.set(false);
      }
    }
  }, [data, dirtySteps, clearStepDirty, onError, saveStepToBackend]);

  /**
   * Persists every step in `pendingStepIndices` sequentially, holding a
   * single `saving` / `Busy` overlay open for the whole batch instead of each
   * step's own `runStep` call toggling it off and back on. Cross-step
   * enrichment (Autofill) can leave several steps dirty at once, and
   * `goToStep`/`goBack`/`saveAndExit` all persist every pending step on a
   * single navigation — without this wrapper, that meant an off→on flash of
   * the Busy overlay (and a discrete `saving` re-render) between every step
   * in the batch.
   *
   * @param mode Forwarded to each `runStep` call — see {@link StepSaveMode}.
   *        `enforce` stops the batch on the first invalid step so the caller
   *        can surface its errors and keep the user on it; `skipInvalid` and
   *        `saveIncomplete` never block, and the batch keeps going so later
   *        dirty steps aren't stranded unsaved.
   * @returns `'saved'` once every step persists (or is validly skipped);
   *        `'error'` (with the failing step) on the first network failure,
   *        which always stops the batch immediately so the user can retry;
   *        `'invalid'` (with the first invalid step) under `enforce` — that
   *        stops the batch too — or, under the non-blocking modes, if every
   *        pending step was attempted but at least one was invalid
   *        (informational only; those callers don't treat it as blocking).
   */
  const persistPendingSteps = useCallback(async (
    pendingStepIndices: number[],
    mode: StepSaveMode
  ): Promise<{ result: StepSaveResult; failedStep?: number }> => {
    const hasLocationId = Boolean(persistedLocationIdRef.current || data.id);
    const willPersistSomething = pendingStepIndices.some(
      (stepIndex) => getStepPersistability(
        stepIndex,
        data,
        dirtySteps.has(stepIndex),
        hasLocationId,
        { allowIncomplete: mode === 'saveIncomplete' }
      ).willPersist
    );
    if (willPersistSomething) {
      setSaving(true);
      Busy.set(true);
    }
    try {
      let firstInvalidStep: number | undefined;
      for (const stepIndex of pendingStepIndices) {
        const result = await runStep(stepIndex, mode, false);
        if (result === 'error') {
          // Always stop immediately — a network failure should keep the user
          // in place so they can retry, regardless of mode.

          return { result, failedStep: stepIndex };
        }
        if (result === 'invalid') {
          if (mode === 'enforce') {
            // Blocking: stop here so the caller surfaces this step's errors
            // and keeps the user on it.
            return { result, failedStep: stepIndex };
          }
          // Free navigation: this step is skipped (never blocks), but other
          // pending dirty steps must still be attempted — stopping here would
          // silently strand them unsaved even though navigation proceeds.
          if (firstInvalidStep === undefined) firstInvalidStep = stepIndex;
        }
      }
      return firstInvalidStep !== undefined
        ? { result: 'invalid', failedStep: firstInvalidStep }
        : { result: 'saved' };
    } finally {
      if (willPersistSomething) {
        setSaving(false);
        Busy.set(false);
      }
    }
  }, [data, dirtySteps, runStep]);

  /**
   * Commits the admin "Location Live" toggle through the dedicated
   * activate/deactivate endpoints, separately from the Step 6 field save so it
   * can run on Finish even when the admin-settings fields are pristine. The
   * publish state must always be reconciled (and any activation failure
   * surfaced) — never skipped just because Step 6 had no field edits, which is
   * what previously let a repeated Finish silently leave a listing a draft. An
   * admin can take a location live from any status; the API enforces the go-live
   * rules and rejects an ineligible request (e.g. a missing rate). Deactivate
   * only runs when the location is currently live (the API rejects deactivating
   * a never-live draft).
   */
  const reconcilePublishState = useCallback(async (locationId: string) => {
    if (data.live) {
      await activateLocation(locationId);
      persistedLiveRef.current = true;
    } else if (persistedLiveRef.current) {
      const deactivatePayload: LocationDeactivateVO = {
        reasonDeactivated: 'OTHER',
        reasonDeactivatedNote: 'Deactivated by admin'
      };
      await deactivateLocation(locationId, deactivatePayload);
      persistedLiveRef.current = false;
    }
  }, [data.live]);

  const saveDraftAndAdvance = useCallback(async () => {
    if (saving) return;

    // --- Address confirmation gate for Step 1 ---
    // When advancing away from Info (or finishing with Info pending), run the
    // async geocode check before synchronous validation / persist. Reads
    // `dataRef.current` (not the closed-over `data`) so this callback's
    // identity doesn't depend on the whole `data` object — see `dataRef`.
    if (currentStep === WIZARD_STEP_INFO && needsAddressConfirmation(dataRef.current)) {
      const outcome = await runAddressGate(dataRef.current, setStepErrors, onError);
      switch (outcome.kind) {
        case 'blocked':
          return;
        case 'confirming':
          // Dialog is now open. Stash the action so the post-confirm effect
          // can re-enter saveDraftAndAdvance after data updates.
          pendingAdvanceActionRef.current = 'next';
          return;
        case 'resolved':
          // Silently matched — apply the geocoded address and proceed.
          // We need to update data first, then re-enter on the next render
          // so runStep sees the resolved coordinates.
          setData((prev) => ({ ...prev, ...outcome.update }));
          markStepDirty(WIZARD_STEP_INFO);
          pendingAdvanceActionRef.current = 'next';
          return;
        case 'skipped':
          // Already resolved (should not happen since needsAddressConfirmation
          // was true), but fall through to normal flow.
          break;
      }
    }

    // Intermediate steps ("Next"): save just the current step and advance.
    if (currentStep < lastStepIndex) {
      // Next matches the step indicator: it never traps the supplier on an
      // unfinished page. The listing still can't be submitted for review until
      // every required field is filled, and the indicator leaves an incomplete
      // step unchecked so what's outstanding stays visible.
      const incomplete = Object.keys(validateStep(currentStep, data)).length > 0;
      const result = await runStep(currentStep, resolveSaveMode());
      if (result !== 'saved') return;
      if (incomplete) {
        onWarning(`${steps[currentStep]} saved — required fields are still missing.`);
      }
      markVisited(currentStep);
      // Record the advance so steps that earn their checkmark by being advanced
      // past (Manager, Features) get checked when Next is clicked. Steps with
      // required fields derive completion from validation, so this is a harmless
      // no-op for them.
      markAdvanced(currentStep);
      setCurrentStep((step) => Math.min(step + 1, lastStepIndex));
      return;
    }

    // Final step ("Finish"). Persist EVERY pending step — not just the last one
    // — with validation enforced. Unlike Next, Finish is terminal: it exits the
    // wizard, and for an admin it can also take the listing live, which the API
    // rejects outright when required fields are missing. A step the user
    // advanced past while incomplete therefore blocks here and surfaces its
    // errors on that step. Drafts reach the end via Submit for Review, which
    // enforces the same way.
    const pendingStepIndices = getPendingStepIndices(currentStep, dirtySteps, { currentFirst: false });
    const { result, failedStep } = await persistPendingSteps(pendingStepIndices, 'enforce');
    if (result !== 'saved') {
      if (result === 'invalid' && failedStep !== undefined) {
        setCurrentStep(failedStep);
        markVisited(failedStep);
      }
      return;
    }

    // Commit the admin "Location Live" toggle. This runs on every admin Finish
    // where a publish change is actually needed, so the publish state is always
    // reconciled and a failed activation (e.g. a location missing a rate) is
    // surfaced instead of silently leaving the listing a draft. When neither an
    // activate nor a deactivate is required (a draft staying a draft), skip the
    // network call and the Busy overlay to avoid an unnecessary flicker.
    if (isAdmin) {
      const locationId = (persistedLocationIdRef.current || dataRef.current.id) as string | undefined;
      const shouldReconcilePublishState = Boolean(locationId) && (dataRef.current.live || persistedLiveRef.current);
      if (shouldReconcilePublishState) {
        setSaving(true);
        Busy.set(true);
        try {
          await reconcilePublishState(locationId as string);
        } catch (err) {
          onError(getErrorMessage(err, 'Something went wrong updating the location. Please try again.'));
          return;
        } finally {
          setSaving(false);
          Busy.set(false);
        }
      }
    }

    markVisited(currentStep);
    markAdvanced(currentStep);
    // The location stays in its current status (a live listing stays live).
    navigate(LOCATIONS_PROFILE_PATH);
  }, [currentStep, data, lastStepIndex, dirtySteps, isAdmin, steps, runAddressGate, persistPendingSteps, reconcilePublishState, markAdvanced, markVisited, markStepDirty, navigate, onError, onWarning, resolveSaveMode, runStep, saving]);

  /**
   * Footer "Save" action. Validates and persists every pending step, then keeps
   * the user exactly where they are so they can carry on editing — or leave via
   * Close, which no longer prompts now that nothing is dirty. An invalid step
   * blocks the save, surfaces its errors, and becomes the active step.
   *
   * Saves every *dirty* step rather than only the visible one because enrichment
   * (Autofill) and step jumps can leave several dirty at once; the footer button
   * is gated on the same `isDirty` that covers them all. A pristine current step
   * is left out entirely — see the batch comment below.
   */
  const save = useCallback(async () => {
    if (saving) return;

    // Mirrors saveDraftAndAdvance / saveAndExit: a manually-typed address on
    // Step 1 must clear the geocode gate before it can be persisted.
    if (currentStep === WIZARD_STEP_INFO && needsAddressConfirmation(dataRef.current)) {
      const outcome = await runAddressGate(dataRef.current, setStepErrors, onError);
      switch (outcome.kind) {
        case 'blocked':
          return;
        case 'confirming':
          pendingAdvanceActionRef.current = 'save';
          return;
        case 'resolved':
          setData((prev) => ({ ...prev, ...outcome.update }));
          markStepDirty(WIZARD_STEP_INFO);
          pendingAdvanceActionRef.current = 'save';
          return;
        case 'skipped':
          break;
      }
    }

    // Dirty steps only, ascending — deliberately NOT getPendingStepIndices,
    // which always folds in the current step. That is right for Close → Save
    // (an incomplete visible step must not silently exit) but wrong here:
    // `runStep` validates before it checks dirtiness, so including a pristine
    // invalid current step would abort the batch and strand real edits on
    // other steps unsaved. Ascending keeps Step 1 first when it still has to
    // create the location id. Never empty — the button is gated on `isDirty`.
    const pendingStepIndices = Array.from(dirtySteps).sort((left, right) => left - right);
    const { result, failedStep } = await persistPendingSteps(pendingStepIndices, resolveSaveMode());
    if (result !== 'saved') {
      setCurrentStep(failedStep ?? currentStep);
      return;
    }
    // Keep the visible page's inline errors truthful: saving an incomplete step
    // is allowed, so the required-field cues must survive the save.
    const remainingErrors = validateStep(currentStep, dataRef.current);
    setStepErrors(remainingErrors);
    if (Object.keys(remainingErrors).length > 0) {
      onWarning('Saved. Some required fields are still missing — complete them to submit for review.');
      return;
    }
    onSuccess('Your changes have been saved.');
  }, [currentStep, dirtySteps, markStepDirty, onError, onSuccess, onWarning, persistPendingSteps, resolveSaveMode, runAddressGate, saving]);

  /**
   * Footer "Submit for Review" action (suppliers, draft listings). Validates
   * every step first and jumps to the first incomplete one if any fail; a
   * listing must be complete before it can be reviewed. On success it saves the
   * current step, submits the location for SecurSpace review, and exits.
   */
  const submitForReview = useCallback(async () => {
    if (saving || submitInFlightRef.current) return;
    submitInFlightRef.current = true;

    try {
      for (let index = 0; index <= lastStepIndex; index++) {
        const validationErrors = validateStep(index, data);
        if (Object.keys(validationErrors).length > 0) {
          setStepErrors(validationErrors);
          setCurrentStep(index);
          markVisited(index);
          onError('Please complete all required fields before submitting for review.');
          return;
        }
      }

      // The Manager step requires the selected manager to have a phone number.
      // validateStep doesn't cover this, so enforce it here (mirrors runStep) and
      // send the user back to the Manager step to fix it.
      if (data.locationManager && !data.locationManager.phoneNumber) {
        setCurrentStep(2);
        markVisited(2);
        onError('The selected location manager is missing a phone number. Please choose a different manager or update their profile first.');
        return;
      }

      const result = await runStep(currentStep, 'enforce');
      if (result !== 'saved') return;

      if (!data.id) {
        onError('Please complete Step 1 first.');
        return;
      }

      setSaving(true);
      Busy.set(true);
      try {
        await submitLocationForReview(data.id);
        navigate(LOCATIONS_PROFILE_PATH);
      } catch (err) {
        onError(getErrorMessage(err, 'Something went wrong submitting your listing for review. Please try again.'));
      } finally {
        setSaving(false);
        Busy.set(false);
      }
    } finally {
      submitInFlightRef.current = false;
    }
  }, [currentStep, data, lastStepIndex, markVisited, navigate, onError, runStep, saving]);


  const goToStep = useCallback(async (targetStep: number) => {
    if (saving || targetStep === currentStep || targetStep < 0 || targetStep > lastStepIndex) {
      return;
    }
    // Persist every dirty step, not only the one being left. Enrichment (the
    // admin "Autofill Location" feature) can populate Location Settings (Step
    // 2) and Features (Step 4) while Info (Step 1) is active, so a jump from
    // Info to any other step must not strand those steps' unsynced data.
    // Ascending order (not current-first) ensures Step 1 — which creates the
    // location id every later step's save depends on — always runs first if
    // it's still dirty. Validation never blocks free navigation; only a network
    // error keeps the user in place so they can retry. Entered values are kept
    // in React state regardless.
    const pendingStepIndices = getPendingStepIndices(currentStep, dirtySteps, { currentFirst: false });
    const { result } = await persistPendingSteps(pendingStepIndices, 'skipInvalid');
    if (result === 'error') return;
    markVisited(currentStep);
    setCurrentStep(targetStep);
  }, [currentStep, dirtySteps, lastStepIndex, markVisited, persistPendingSteps, saving]);

  const goBack = useCallback(async () => {
    if (saving || currentStep === 0) return;
    setStepErrors({});
    // Persist every dirty step before stepping back — see goToStep for why a
    // single-step save isn't sufficient when cross-step enrichment is dirty.
    // Validation never blocks going back; only a network error keeps the user
    // in place so they can retry. Local edits remain in React state.
    const pendingStepIndices = getPendingStepIndices(currentStep, dirtySteps, { currentFirst: false });
    const { result } = await persistPendingSteps(pendingStepIndices, 'skipInvalid');
    if (result === 'error') return;
    setCurrentStep((step) => Math.max(step - 1, 0));
  }, [currentStep, dirtySteps, persistPendingSteps, saving]);

  const saveAndExit = useCallback(async () => {
    if (saving) return;

    // --- Address confirmation gate for Step 1 ---
    // When saveAndExit would persist Step 1 with a manually-typed address, run
    // the async geocode check first, mirroring saveDraftAndAdvance. Reads
    // `dataRef.current` (not the closed-over `data`) so this callback's
    // identity doesn't depend on the whole `data` object — see `dataRef`.
    if (currentStep === WIZARD_STEP_INFO && needsAddressConfirmation(dataRef.current)) {
      const outcome = await runAddressGate(dataRef.current, setStepErrors, onError);
      switch (outcome.kind) {
        case 'blocked':
          return;
        case 'confirming':
          pendingAdvanceActionRef.current = 'saveAndExit';
          return;
        case 'resolved':
          setData((prev) => ({ ...prev, ...outcome.update }));
          markStepDirty(WIZARD_STEP_INFO);
          pendingAdvanceActionRef.current = 'saveAndExit';
          return;
        case 'skipped':
          break;
      }
    }

    // Save every pending step, not only the currently visible one. Enrichment
    // can populate Settings and Features while Info is active; a step-number
    // jump saves Info first and intentionally leaves those other steps dirty.
    // Missing required fields no longer block here: a draft is incomplete by
    // definition, and blocking left suppliers looping between "Save changes"
    // and an unfinished page with only "Discard changes" as a way out.
    // Close → Save persists field edits (e.g. a deleted image) but must never
    // (un)publish — publishing is committed only by footer Finish, via
    // reconcilePublishState.
    const pendingStepIndices = getPendingStepIndices(currentStep, dirtySteps, { currentFirst: true });
    const { result, failedStep } = await persistPendingSteps(pendingStepIndices, resolveSaveMode());
    if (result !== 'saved') {
      setCurrentStep(failedStep ?? currentStep);
      return;
    }
    navigate(LOCATIONS_PROFILE_PATH);
  }, [currentStep, dirtySteps, runAddressGate, markStepDirty, navigate, onError, persistPendingSteps, resolveSaveMode, saving]);

  // Keep refs pointing at the latest saveDraftAndAdvance/saveAndExit closures.
  // The effect below re-enters the advance flow after an address confirmation
  // resolves, and must always call the current version of these callbacks —
  // not a stale one captured when the pending action was first queued — since
  // either callback can be recreated (new `useCallback` identity) by an
  // unrelated re-render in between.
  const saveDraftAndAdvanceRef = useRef(saveDraftAndAdvance);
  useEffect(() => {
    saveDraftAndAdvanceRef.current = saveDraftAndAdvance;
  }, [saveDraftAndAdvance]);

  const saveAndExitRef = useRef(saveAndExit);
  useEffect(() => {
    saveAndExitRef.current = saveAndExit;
  }, [saveAndExit]);

  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  // After an address confirmation dialog resolves (user picks 'suggested' or
  // 'typed'), the geocoded coords are applied to `data` via `setData`. On the
  // next render `data` has the fresh coords, `needsAddressConfirmation` returns
  // false, and we can safely re-enter the advance flow. This effect fires once
  // per pending action and then clears the ref. It calls the ref-stored
  // callbacks (not the closed-over `saveDraftAndAdvance`/`saveAndExit`
  // directly) so it never invokes a stale version of either.
  useEffect(() => {
    const action = pendingAdvanceActionRef.current;
    if (!action) return;
    // Wait until the address is actually resolved (coords present). The effect
    // fires on every `data` change, but only proceeds when coords are set.
    if (needsAddressConfirmation(data)) return;

    pendingAdvanceActionRef.current = null;
    if (action === 'next') {
      void saveDraftAndAdvanceRef.current();
    } else if (action === 'save') {
      void saveRef.current();
    } else if (action === 'saveAndExit') {
      void saveAndExitRef.current();
    }
  }, [data]);

  const discardAndExit = useCallback(() => {
    // Leave without saving the current step's unsynced edits. Earlier steps were
    // already persisted as the user advanced.
    navigate(LOCATIONS_PROFILE_PATH);
  }, [navigate]);

  // A step earns its checkmark only when it has been visited AND currently has no
  // validation errors. Steps 3 (Manager) and 4 (Features) have no required
  // fields, so validation alone would check them off the moment they're visited.
  // Instead — like the admin-only Step 6 (Admin Settings) — they're checked only
  // when recorded in `advancedSteps`: in create mode that happens when the user
  // clicks Next on the step, so a blank step stays unchecked until actively
  // advanced past; in edit mode every step starts advanced (the location was
  // already completed), so an already-entered step shows checked on load.
  const completedSteps = useMemo(
    () =>
      visitedSteps.filter((index) => {
        if (Object.keys(validateStep(index, data)).length > 0) return false;
        if (index === 2 || index === 3 || index === 5) {
          return advancedSteps.includes(index);
        }
        return true;
      }),
    [visitedSteps, advancedSteps, data]
  );

  const footerConfig = getWizardFooterConfig({
    isAdmin,
    currentStep,
    lastStepIndex,
    isLive: data.live,
  });

  return {
    isEdit,
    isAdmin,
    loading,
    saving,
    data,
    currentStep,
    steps,
    lastStepIndex,
    visitedSteps,
    completedSteps,
    stepErrors,
    listingFile,
    galleryFiles,
    galleryReplacements,
    footerConfig,
    updateData,
    applyEnrichmentData,
    setListingFile,
    setGalleryFiles,
    replaceGalleryImage,
    saveDraftAndAdvance,
    save,
    submitForReview,
    goToStep,
    goBack,
    isDirty,
    checkingAddress: addressGate.checkingAddress,
    addressConfirmationDialog: addressGate.confirmationDialog,
    handleAddressConfirm,
    handleAddressConfirmClose,
    saveAndExit,
    discardAndExit,
  };
}




